import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  Typography
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import withStyles from '@material-ui/core/styles/withStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SaveIcon from '@material-ui/icons/Save';
import SearchIcon from '@material-ui/icons/Search';
import { Formik, useFormikContext } from 'formik';
import { withSnackbar } from 'notistack';
import React, { useRef, useState } from 'react';
import PartidaArancelariaSelector from '../../../components/PartidaArancelariaSelector';
import AutocompleteWithFormik from '../../../components/inputs/AutocompleteWithFormik';
import InputWithFormik from '../../../components/inputs/InputWithFormik';
import NumberInput from '../../../components/inputs/NumberInput';
import { Yup, formatNumber, schemaCurrency } from '../../../utils/form-utils';
import FormikSubmitEffect from './FormikSubmitEffect';

const styles = theme => ({
  innerContainer: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      padding: 0
    }
  },
  innerContainerBeneficiario: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(3)
  },
  textInnerContainerBeneficiario: {
    fontSize: 15,
    color: '#686b69',
    paddingLeft: 5
  },
  leftPadding: {
    paddingLeft: theme.spacing(3 / 2),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0
    }
  },
  rightPadding: {
    paddingRight: theme.spacing(3 / 2),
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  }
});

const validationSchema = Yup.object().shape({
  partidaArancelaria: Yup.string().required(),
  descripcion: Yup.string().required(),
  cantidad: Yup.string().required(),
  valorVenta: schemaCurrency(),
  peso: Yup.string().required(),
  unidad: Yup.object().shape({
    id: Yup.string().required()
  }),
  factura: Yup.object().shape({
    id: Yup.string().required()
  }),
  bulto: Yup.string().required()
});

const MercaderiaFields = ({ facturas, unidadMedidas, moneda }) => {
  const { setFieldValue, values } = useFormikContext();
  const [openActividades, setOpenActividades] = useState(false);

  return (
    <>
      <Grid item xs={12} md={12}>
        <AutocompleteWithFormik
          required
          label="Factura correspondiente"
          name="factura"
          options={(facturas || []).map(item => ({
            ...item,
            label: `${item.nroFactura} - ${formatNumber(item.montoFactura)}`
          }))}
          labelKey="label"
          valueKey="id"
          isObjectValue
        />
      </Grid>
      <PartidaArancelariaSelector
        onChange={(value, descripcion, detalle) => {
          setFieldValue('partidaArancelaria', value);
          if (descripcion) {
            setFieldValue('descripcion', descripcion);
          }
          if (detalle) {
            setFieldValue('detalle', detalle);
          }
        }}
        open={openActividades}
        onClose={() => setOpenActividades(false)}
      />
      <Grid item xs={12} md={12}>
        <InputWithFormik
          required
          label="Partida Arancelaria"
          name="partidaArancelaria"
          value={
            values.partidaArancelaria &&
            `${values.partidaArancelaria.codigo}${
              values.partidaArancelaria.esHoja
                ? values.partidaArancelaria.digitoVerificador || ''
                : ''
            }. ${values.partidaArancelaria.nombreResumido}`
          }
          placeholder="Seleccione..."
          onClick={() => {
            setOpenActividades(true);
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            )
          }}
        />
      </Grid>

      {values.detalle && (
        <Grid item xs={12} md={12} style={{ paddingTop: 0 }}>
          <Typography>Partida arancelaria seleccionada</Typography>
          {values.detalle}
        </Grid>
      )}

      <Grid item xs={12} md={12}>
        <InputWithFormik label="Descripción" name="descripcion" required />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputWithFormik label="Cantidad" name="cantidad" required />
      </Grid>
      <Grid item xs={12} md={6}>
        <AutocompleteWithFormik
          required
          label="Unidad de Medida"
          name="unidad"
          options={unidadMedidas}
          labelKey="descripcion"
          valueKey="id"
          isObjectValue
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputWithFormik
          required
          label={`Valor de venta actual (${moneda?.descripcion ||
            'Sin definir'})`}
          name="valorVenta"
          InputProps={{
            inputComponent: NumberInput
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputWithFormik
          label="Peso incluyendo el embalaje en Kg"
          name="peso"
          required
          InputProps={{
            inputComponent: NumberInput
          }}
        />
        <FormHelperText variant="outlined">
          Por ejemplo: Si la carga pesa “750 gramos”, debe introducir “0,75”.
        </FormHelperText>
      </Grid>
      <Grid item xs={12} md={6}>
        <InputWithFormik
          label="Cantidad de bultos"
          name="bulto"
          required
          InputProps={{
            inputComponent: NumberInput,
            inputProps: { decimalScale: 0 }
          }}
        />
      </Grid>
    </>
  );
};

const MercaderiaFieldsWhitSnackbar = withSnackbar(MercaderiaFields);

const MercaderiasAddForm = ({
  enqueueSnackbar,
  config,
  onClose,
  onChange,
  classes,
  facturas,
  unidadMedidas,
  moneda
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { open, defaultValue, isEdit, disabled } = config;
  const mercaderiaConfig = {
    classes,
    isEdit,
    disabled
  };

  const onSubmitError = ({ errors }) => {
    // Validacion de Lista de Socios de la Persona Juridica
    const key = Object.keys(errors).length;
    if (key === 1 && errors.terceroJuridicas) {
      enqueueSnackbar(errors.terceroJuridicas, {
        variant: 'warning'
      });
      return;
    }

    enqueueSnackbar('Verificar formatos y campos obligatorios', {
      variant: 'warning'
    });
  };

  const onSubmit = values => {
    if (onChange) {
      onChange(values);
    }
    if (onClose) {
      onClose();
    }
  };

  const formik = useRef();

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      scroll="paper"
      fullWidth
      fullScreen
      onClose={onClose}
      disableEscapeKeyDown
      classes={{
        paper: classes.dialogPaper
      }}
    >
      <DialogTitle>
        {isEdit ? 'Editar' : 'Nueva'}
        {' Mercadería'}
      </DialogTitle>
      <DialogContent dividers>
        <Formik
          enableReinitialize
          initialValues={{
            ...defaultValue
          }}
          innerRef={formik}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {() => (
            <>
              <FormikSubmitEffect onSubmitError={onSubmitError} />
              <Container maxWidth="md" className={classes.innerContainer}>
                <Grid container spacing={3}>
                  <MercaderiaFieldsWhitSnackbar
                    {...mercaderiaConfig}
                    facturas={facturas}
                    unidadMedidas={unidadMedidas}
                    moneda={moneda}
                  />
                </Grid>
              </Container>
            </>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          size={isPhone ? 'small' : 'medium'}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          size={isPhone ? 'small' : 'medium'}
          onClick={() => formik.current.submitForm()}
          startIcon={<SaveIcon />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withSnackbar(withStyles(styles)(MercaderiasAddForm));
